export const SMART_ROOM_PATH = "/smartRoomDashboard";

export const ODMSENSORHUB_CATEGORY = "ODMSENSORHUB";

/** Indoor environmental refresh (1 minute). */
export const INDOOR_POLL_INTERVAL_MS = 60_000;

/** Outdoor weather refresh (~30 minutes). */
export const OUTDOOR_POLL_INTERVAL_MS = 30 * 60_000;

/** @deprecated Prefer INDOOR_POLL_INTERVAL_MS / OUTDOOR_POLL_INTERVAL_MS */
export const POLL_INTERVAL_MS = INDOOR_POLL_INTERVAL_MS;

export const AQI_SCALE_BANDS = [
  { key: "good", label: "Good", min: 0, max: 50, color: "#4CAF50" },
  { key: "moderate", label: "Moderate", min: 51, max: 100, color: "#FFEB3B" },
  { key: "poor", label: "Poor", min: 101, max: 200, color: "#FF9800" },
  { key: "unhealthy", label: "Unhealthy", min: 201, max: 300, color: "#F44336" },
  { key: "severe", label: "Severe", min: 301, max: 400, color: "#9C27B0" },
  { key: "hazardous", label: "Hazardous", min: 401, max: 500, color: "#880E4F" },
];

export const STATUS_LABELS = {
  GOOD: "Good",
  MODERATE: "Moderate",
  POOR: "Poor",
  CRITICAL: "Critical",
  NORMAL: "Within Normal Range",
  UNKNOWN: "—",
};

export const PARAMETER_RANGES = {
  aqi: [
    { max: 50, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 100, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 200, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 500, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  pm25: [
    { max: 30, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 60, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 90, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 250, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  pm10: [
    { max: 50, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 100, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 250, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 430, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  pm1: [
    { max: 15, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 35, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 55, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 150, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  temperature: [
    { max: 18, status: STATUS_LABELS.MODERATE, color: "#0288D1" },
    { max: 26, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 32, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 45, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 60, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  humidity: [
    { max: 30, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 60, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 70, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 85, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 100, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  co2: [
    { max: 800, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 1000, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 1500, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 5000, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  tvoc: [
    { max: 0.3, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 0.5, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 1, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 10, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  noise: [
    { max: 40, status: STATUS_LABELS.GOOD, color: "#4CAF50" },
    { max: 55, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 70, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 120, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
  pressure: [
    { max: 980, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 1030, status: STATUS_LABELS.NORMAL, color: "#4CAF50" },
    { max: 1100, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
  ],
  windSpeed: [
    { max: 5, status: STATUS_LABELS.NORMAL, color: "#4CAF50" },
    { max: 12, status: STATUS_LABELS.MODERATE, color: "#FBC02D" },
    { max: 20, status: STATUS_LABELS.POOR, color: "#FF9800" },
    { max: 50, status: STATUS_LABELS.CRITICAL, color: "#D32F2F" },
  ],
};

export const PARAMETER_META = {
  temperatureC: { key: "temperature", label: "Temperature", unit: "°C", rangeKey: "temperature" },
  temperatureF: { key: "temperatureF", label: "Temperature", unit: "°F", rangeKey: "temperature" },
  humidity: { key: "humidity", label: "Humidity", unit: "%", rangeKey: "humidity" },
  aqiIn: { key: "aqiIn", label: "AQI (IN)", unit: "", rangeKey: "aqi" },
  aqiUs: { key: "aqiUs", label: "AQI (US)", unit: "", rangeKey: "aqi" },
  pm1: { key: "pm1", label: "PM1", unit: "µg/m³", rangeKey: "pm1" },
  pm25: { key: "pm25", label: "PM2.5", unit: "µg/m³", rangeKey: "pm25" },
  pm10: { key: "pm10", label: "PM10", unit: "µg/m³", rangeKey: "pm10" },
  co2: { key: "co2", label: "CO₂", unit: "ppm", rangeKey: "co2" },
  tvoc: { key: "tvoc", label: "TVOC", unit: "ppm", rangeKey: "tvoc" },
  noise: { key: "noise", label: "Noise", unit: "dB", rangeKey: "noise" },
  pressure: { key: "pressure", label: "Pressure", unit: "hPa", rangeKey: "pressure" },
  windSpeed: { key: "windSpeed", label: "Wind Speed", unit: "m/s", rangeKey: "windSpeed" },
  windDirection: { key: "windDirection", label: "Wind Direction", unit: "", rangeKey: null },
};

export const ERROR_MESSAGES = {
  INVALID_HOME: "Please enter a valid Home ID.",
  INVALID_SPACE: "Please enter a valid Space ID.",
  HOME_NOT_FOUND: "Home not found. Please check the Home ID and try again.",
  SPACE_NOT_FOUND: "Space not found in this home. Please verify the Space ID.",
  NO_SPACES: "No spaces found for this Home ID.",
  SPACES_LOAD_FAILED: "Unable to load spaces for this Home ID. Please try again.",
  SENSOR_NOT_FOUND:
    "Indoor space parameters are unavailable for this space.",
  DEVICE_NOT_FOUND:
    "Indoor space parameters are unavailable for this space.",
  DEVICE_STATUS_FAILED:
    "Unable to load indoor space parameters. Please try again.",
  WEATHER_FAILED:
    "Unable to load outdoor weather data. Please try again.",
  WEATHER_NETWORK:
    "Unable to reach the Weather API. Check network access and try again.",
  MISSING_COORDINATES:
    "Home location coordinates are unavailable. Outdoor weather cannot be loaded.",
  TIMEOUT: "Request timed out. Please try again.",
  BOTH_FAILED:
    "Unable to load indoor and outdoor data for this space. Please verify the setup and try again.",
  GENERIC: "Something went wrong. Please try again.",
};

export const ADVANCED_FIELD_KEYS = [
  { key: "particle count(0.3)", label: "Particle Count 0.3" },
  { key: "particle count(0.5)", label: "Particle Count 0.5" },
  { key: "particle count(1.0)", label: "Particle Count 1.0" },
  { key: "particle count(3.0)", label: "Particle Count 3.0" },
  { key: "particle count(5.0)", label: "Particle Count 5.0" },
  { key: "particle count(10.0)", label: "Particle Count 10.0" },
  { key: "onlineStatus", label: "Device Status" },
  { key: "serialNo", label: "Serial Number" },
  { key: "connectedTime", label: "Connected Time", isTimestamp: true },
  { key: "updatedTime", label: "Updated Time", isTimestamp: true },
  { key: "nextUpdate", label: "Next Update", isTimestamp: true },
  { key: "deviceName", label: "Device Name" },
  { key: "sid", label: "SID" },
  { key: "ty", label: "Type" },
  { key: "cnt", label: "Connection Type" },
  { key: "ts", label: "Timestamp (ts)", isTimestamp: true },
];

export const MAIN_DASHBOARD_KEYS = new Set([
  "AQI-IN",
  "AQI-US",
  "PM1",
  "PM10",
  "Temp(Fer)",
  "setmp",
  "sehum",
  "sepm25",
  "seco2",
  "setvoc",
  "Noise",
  "onlineStatus",
  "deviceName",
  "serialNo",
  "timeago",
  "updatedTime",
  "connectedTime",
  "nextUpdate",
  "particle count(0.3)",
  "particle count(0.5)",
  "particle count(1.0)",
  "particle count(3.0)",
  "particle count(5.0)",
  "particle count(10.0)",
  "sid",
  "ty",
  "cnt",
  "ts",
]);
